import { motion } from "framer-motion";
import { RotateCcw, FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import generateAnalysisReport from "@/utils/reportGenerator";
import { toPng } from 'html-to-image';

export default function Dashboard({ 
  activeView, 
  analysisData, 
  timeLapseData, 
  chartsPanelRef,
  onReset,
  children 
}) {
  const [isGenerating, setIsGenerating] = useState(false);

  const handleDownloadReport = useCallback(async () => {
    try {
      setIsGenerating(true);
      toast.info("Generating PDF report with current chart...");
      
      // Capture only the currently selected chart
      let allChartImages = [];
      
      if (chartsPanelRef?.current && activeView === '3d') {
        const chartMethods = chartsPanelRef.current;
        const currentTab = chartMethods.getActiveTab?.() || 'histogram';
        const scatterMode = chartMethods.getScatterMode?.() || 'standard';
        
        // Define titles based on current selection
        const tabTitles = {
          histogram: 'Intensity Histogram',
          scatter: scatterMode === 'standard' ? 'Scatter Plot (Standard)' :
                   scatterMode === 'paper' ? 'Scatter Plot (Log Ratio)' :
                   'Scatter Plot (Quadrant Analysis)',
          ccf: 'Cross-Correlation Function (CCF)'
        };
        
        try {
          // Wait for current chart to be fully rendered
          await new Promise(r => setTimeout(r, 500));
          
          // Get refs and capture the currently visible chart
          const refs = chartMethods.getChartRefs?.();
          const chartRef = refs?.[currentTab];
          
          if (chartRef?.current) {
            const element = chartRef.current;
            
            // Ensure element is visible and has dimensions
            if (element.offsetWidth > 0 && element.offsetHeight > 0) {
              try {
                // Use html-to-image which handles SVGs much better
                const imgData = await toPng(element, {
                  backgroundColor: '#0f172a',
                  pixelRatio: 2,
                  cacheBust: true,
                  style: {
                    transform: 'none'
                  }
                });
                
                allChartImages.push({
                  title: tabTitles[currentTab],
                  imgData: imgData,
                  width: element.offsetWidth * 2,
                  height: element.offsetHeight * 2
                });
              } catch (captureErr) {
                console.warn('Chart capture failed:', captureErr);
              }
            }
          }
        } catch (err) {
          console.warn(`Failed to capture ${currentTab}:`, err);
        }
      }
      
      const filename = await generateAnalysisReport(
        analysisData, 
        activeView, 
        timeLapseData, 
        allChartImages
      );
      toast.success(`Report downloaded: ${filename}`);
    } catch (error) {
      console.error('Failed to generate report:', error);
      toast.error("Failed to generate report: " + error.message);
    } finally {
      setIsGenerating(false);
    }
  }, [analysisData, activeView, timeLapseData, chartsPanelRef]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      data-testid="dashboard"
    >
      {/* Action Bar */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-chivo text-2xl font-bold">
            {activeView === "3d" ? "3D Stack Analysis" : "Time-lapse Analysis"}
          </h2>
          <p className="text-muted-foreground text-sm">
            {activeView === "3d" && analysisData?.stack_info && (
              `Stack dimensions: ${analysisData.stack_info.shape.join(" × ")} voxels`
            )}
            {activeView === "timelapse" && timeLapseData && (
              `${timeLapseData.n_frames} frames | Strategy: ${timeLapseData.threshold_strategy}`
            )}
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={handleDownloadReport}
            disabled={isGenerating}
            className="gap-2 rounded-full"
            data-testid="download-report-btn"
          >
            {isGenerating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <FileText className="w-4 h-4" />
            )}
            {isGenerating ? "Generating..." : "Download Report"}
          </Button>
          
          <Button
            variant="outline"
            onClick={onReset}
            className="gap-2 rounded-full"
            data-testid="new-analysis-btn"
          >
            <RotateCcw className="w-4 h-4" />
            New Analysis
          </Button>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-12 gap-6">
        {children}
      </div>
    </motion.div>
  );
}
